import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import { Stethoscope, Building2, Check, X, Loader2 } from "lucide-react";

const passwordRules = (pw) => ({
    len: pw.length >= 8,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
});

function RuleRow({ ok, label }) {
    return (
        <div className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-700" : "text-[#5C5C5C]"}`}>
            {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
            <span>{label}</span>
        </div>
    );
}

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const initialRole = params.get("role") === "facility" ? "facility" : "professional";
    const [role, setRole] = useState(initialRole);
    const [form, setForm] = useState({
        email: "", password: "", full_name: "", phone: "", profession: "", facility_name: ""
    });
    const [professions, setProfessions] = useState([]);
    const [profError, setProfError] = useState(false);
    const [profLoading, setProfLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [profFieldError, setProfFieldError] = useState("");

    const loadProfessions = async () => {
        setProfLoading(true); setProfError(false);
        try {
            const { data } = await api.get("/professions");
            const list = (data.data || []).filter(p => p && p.name && p.name.trim());
            setProfessions(list);
        } catch (e) {
            setProfError(true);
        } finally { setProfLoading(false); }
    };
    useEffect(() => { loadProfessions(); }, []);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const rules = useMemo(() => passwordRules(form.password), [form.password]);
    const passwordOk = rules.len && rules.lower && rules.upper && rules.special;

    const submit = async (e) => {
        e.preventDefault();
        setProfFieldError("");
        if (role === "professional" && !form.profession) {
            setProfFieldError("Seleziona la tua professione");
            return;
        }
        if (!passwordOk) {
            toast.error("La password non rispetta i requisiti di sicurezza");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                email: form.email, password: form.password, role,
                full_name: form.full_name, phone: form.phone || null,
                profession: role === "professional" ? form.profession : null,
                facility_name: role === "facility" ? form.facility_name : null,
            };
            const u = await register(payload);
            toast.success("Registrazione completata!");
            nav(u.role === "professional" ? "/app/opportunita" : "/app/annunci", { replace: true });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Errore di registrazione");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[#F9F9F7] py-10">
            <div className="max-w-2xl mx-auto px-6">
                <Link to="/" className="font-display text-2xl font-bold">HealthLink</Link>
                <div className="mt-6 hl-card p-8">
                    <h1 className="font-display text-3xl font-bold tracking-tight">Crea il tuo account</h1>
                    <p className="mt-2 text-sm text-[#5C5C5C]">Base gratuita. Nessuna carta richiesta.</p>

                    <div className="mt-6 grid grid-cols-2 gap-3">
                        <button type="button" onClick={() => setRole("professional")}
                            data-testid="role-professional-btn"
                            className={`p-4 rounded-lg border transition-colors text-left ${role === "professional" ? "border-[#1C3F39] bg-[#EEF3F1]" : "border-[#E5E5E0] hover:border-[#1C3F39]/40"}`}>
                            <Stethoscope className="w-5 h-5 text-[#1C3F39]" />
                            <div className="mt-2 font-display font-bold">Professionista</div>
                            <div className="text-xs text-[#5C5C5C]">Cerca collaborazioni</div>
                        </button>
                        <button type="button" onClick={() => setRole("facility")}
                            data-testid="role-facility-btn"
                            className={`p-4 rounded-lg border transition-colors text-left ${role === "facility" ? "border-[#1C3F39] bg-[#EEF3F1]" : "border-[#E5E5E0] hover:border-[#1C3F39]/40"}`}>
                            <Building2 className="w-5 h-5 text-[#1C3F39]" />
                            <div className="mt-2 font-display font-bold">Struttura</div>
                            <div className="text-xs text-[#5C5C5C]">Pubblica opportunità</div>
                        </button>
                    </div>

                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
                        <div>
                            <Label htmlFor="full_name">{role === "facility" ? "Nome referente" : "Nome e cognome"}</Label>
                            <Input id="full_name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)}
                                required data-testid="register-fullname-input" className="mt-1" />
                        </div>
                        {role === "facility" && (
                            <div>
                                <Label htmlFor="facility_name">Denominazione struttura</Label>
                                <Input id="facility_name" value={form.facility_name} onChange={(e) => update("facility_name", e.target.value)}
                                    required data-testid="register-facility-name-input" className="mt-1" />
                            </div>
                        )}
                        {role === "professional" && (
                            <div>
                                <Label>Professione</Label>
                                {profError ? (
                                    <div className="mt-1 p-3 border border-[#D96C5B]/30 bg-[#FDF4F2] rounded-md flex items-center justify-between gap-3" data-testid="prof-error-box">
                                        <span className="text-sm text-[#D96C5B]">Impossibile caricare le professioni.</span>
                                        <Button type="button" size="sm" variant="outline" onClick={loadProfessions}>Riprova</Button>
                                    </div>
                                ) : profLoading ? (
                                    <div className="mt-1 h-10 border border-[#E5E5E0] rounded-md flex items-center gap-2 px-3 text-sm text-[#5C5C5C]" data-testid="prof-loading">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
                                    </div>
                                ) : (
                                    <Select value={form.profession || undefined} onValueChange={(v) => { update("profession", v); setProfFieldError(""); }}>
                                        <SelectTrigger className={`mt-1 ${profFieldError ? "border-[#D96C5B]" : ""}`} data-testid="register-profession-select">
                                            <SelectValue placeholder="Seleziona la tua professione" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {professions.map((p) => (
                                                <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                                {profFieldError && <p className="mt-1 text-xs text-[#D96C5B]" data-testid="prof-field-error">{profFieldError}</p>}
                            </div>
                        )}
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="email">Email</Label>
                                <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)}
                                    required data-testid="register-email-input" className="mt-1" />
                            </div>
                            <div>
                                <Label htmlFor="phone">Telefono</Label>
                                <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)}
                                    data-testid="register-phone-input" className="mt-1" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input id="password" type="password" value={form.password} onChange={(e) => update("password", e.target.value)}
                                required data-testid="register-password-input" className="mt-1" />
                            <div className="mt-2 grid grid-cols-2 gap-1" data-testid="password-rules">
                                <RuleRow ok={rules.len} label="Almeno 8 caratteri" />
                                <RuleRow ok={rules.lower} label="Una minuscola" />
                                <RuleRow ok={rules.upper} label="Una maiuscola" />
                                <RuleRow ok={rules.special} label="Un carattere speciale" />
                            </div>
                        </div>

                        <Button type="submit" disabled={busy || !passwordOk || (role === "professional" && !form.profession) || profError}
                            className="w-full bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="register-submit-btn">
                            {busy ? "Creazione…" : "Crea account"}
                        </Button>
                        <p className="text-xs text-[#5C5C5C] text-center">
                            Registrandoti accetti Termini e Privacy Policy.
                        </p>
                    </form>
                </div>
                <p className="mt-4 text-sm text-[#5C5C5C] text-center">
                    Hai già un account?{" "}
                    <Link to="/login" className="text-[#1C3F39] font-medium" data-testid="register-to-login-link">Accedi</Link>
                </p>
            </div>
        </div>
    );
}
